import { useState, useCallback, useEffect, useRef } from "react";
import type { SimpleTableConfig, BaseEntity } from "../types";
import type { QuickFilterState } from "@/components/shared/DataTable-modular/types";
import { useTablePrefs } from "@/lib/column-manager/useTablePrefs";

interface UseSimpleTableStateProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  // Pagination (optional)
  propCurrentPage?: number;
  propOnPageChange?: (page: number) => void;
  propPageSize?: number;
  propOnPageSizeChange?: (size: number) => void;
  contextColumnOrder: string[];
  contextSetColumnOrder: (order: string[]) => void;
  /**
   * Per-table preferences key (from the ColumnManager). When set, column WIDTH
   * and PAGE SIZE are seeded from and persisted to the user's per-table prefs in
   * `app_users.user_prefs` (via `useTablePrefs`), so they survive reload/logout
   * and follow the user across devices.
   */
  tablePrefsKey?: string;
}

/**
 * Hook that manages UI state for SimpleTable.
 * Filter state is now managed exclusively by FilterContext.
 * This hook only handles: sorting, pagination, selection, columns, inline editing.
 */
export function useSimpleTableState<TData extends BaseEntity>({
  config,
  propCurrentPage,
  propOnPageChange,
  propPageSize,
  propOnPageSizeChange,
  contextColumnOrder,
  contextSetColumnOrder,
  tablePrefsKey,
}: UseSimpleTableStateProps<TData>) {
  // Per-user, per-table prefs for column width + page size (server-backed).
  const {
    columnWidths: prefColumnWidths,
    pageSize: prefPageSize,
    isHydrated: prefsHydrated,
    persistColumnWidths,
    persistPageSize,
  } = useTablePrefs(tablePrefsKey);
  // ============================================================================
  // SORTING STATE
  // ============================================================================

  const [sortField, setSortField] = useState<string>(config.defaultSort?.field || "");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    config.defaultSort?.direction || "desc"
  );
  const [sortColumns, setSortColumns] = useState<
    Array<{ field: string; direction: "asc" | "desc"; priority: number }>
  >([]);

  // ============================================================================
  // SELECTION STATE
  // ============================================================================

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showFloatingBulkActions, setShowFloatingBulkActions] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  // ============================================================================
  // COLUMN MANAGEMENT STATE
  // ============================================================================

  const buildDefaultWidths = useCallback((): Record<string, number> => {
    const defaultWidths: Record<string, number> = {};
    Object.entries(config.fields).forEach(([fieldId, fieldConfig]) => {
      if (fieldConfig.formOnly) return; // Skip form-only fields in table widths
      defaultWidths[fieldId] = fieldConfig.width || 150;
    });
    return defaultWidths;
  }, [config.fields]);

  const [columnWidths, setColumnWidthsRaw] =
    useState<Record<string, number>>(buildDefaultWidths);

  // True once a user gesture (resize / reset) has changed widths — suppresses
  // persisting the one-time hydration-apply back to the server.
  const widthTouchedRef = useRef(false);
  const widthsHydratedRef = useRef(false);

  // Wrapped setter: any consumer-driven width change (resize drag, reset) marks
  // the widths as user-touched so the debounced effect below persists them.
  const setColumnWidths = useCallback<typeof setColumnWidthsRaw>((update) => {
    widthTouchedRef.current = true;
    setColumnWidthsRaw(update);
  }, []);

  // Seed widths from the user's saved prefs once they hydrate (merged over the
  // config defaults so new/absent columns keep a sensible width). Runs once.
  useEffect(() => {
    if (!prefsHydrated || widthsHydratedRef.current) return;
    widthsHydratedRef.current = true;
    if (prefColumnWidths) {
      const defaults = buildDefaultWidths();
      const merged: Record<string, number> = { ...defaults };
      for (const id of Object.keys(defaults)) {
        if (typeof prefColumnWidths[id] === "number") {
          merged[id] = prefColumnWidths[id];
        }
      }
      setColumnWidthsRaw(merged); // raw setter — not a user gesture, don't persist
    }
  }, [prefsHydrated, prefColumnWidths, buildDefaultWidths]);

  // Persist widths after the user settles (debounced so a resize drag — which
  // streams many updates — only writes once the drag stops). Never persists the
  // hydration-apply above.
  useEffect(() => {
    if (!prefsHydrated || !widthTouchedRef.current || !tablePrefsKey) return;
    const timer = setTimeout(() => persistColumnWidths(columnWidths), 600);
    return () => clearTimeout(timer);
  }, [columnWidths, prefsHydrated, tablePrefsKey, persistColumnWidths]);

  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(() =>
    Object.keys(config.fields).filter((id) => !config.fields[id]?.formOnly)
  );

  const columnOrder = contextColumnOrder.length > 0 ? contextColumnOrder : localColumnOrder;
  const setColumnOrder = contextSetColumnOrder || setLocalColumnOrder;

  const [savedView, setSavedView] = useState<string>("");
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  // ============================================================================
  // INLINE EDITING STATE
  // ============================================================================

  const [editingCell, setEditingCell] = useState<{ rowId: string; fieldId: string } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [savingCell, setSavingCell] = useState<{ rowId: string; fieldId: string } | null>(null);

  // ============================================================================
  // PAGINATION STATE
  // ============================================================================

  const [internalCurrentPage, setInternalCurrentPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(config.defaultPageSize || 25);

  // Seed page size from the user's saved prefs once they hydrate. Runs once, and
  // only for internal (non-prop-driven) pagination.
  const pageSizeHydratedRef = useRef(false);
  useEffect(() => {
    if (!prefsHydrated || pageSizeHydratedRef.current) return;
    pageSizeHydratedRef.current = true;
    if (typeof prefPageSize === "number" && !propOnPageSizeChange) {
      setInternalPageSize(prefPageSize);
    }
  }, [prefsHydrated, prefPageSize, propOnPageSizeChange]);

  const effectiveCurrentPage = propCurrentPage ?? internalCurrentPage;
  const effectiveSetCurrentPage = useCallback(
    (page: number) => {
      if (propOnPageChange) {
        propOnPageChange(page);
      } else {
        setInternalCurrentPage(page);
      }
    },
    [propOnPageChange]
  );

  const effectivePageSize = propPageSize ?? internalPageSize;
  const effectiveSetPageSize = useCallback(
    (size: number) => {
      if (propOnPageSizeChange) {
        propOnPageSizeChange(size);
      } else {
        setInternalPageSize(size);
        // Persist the user's page-size choice for this table (server-backed).
        if (tablePrefsKey) persistPageSize(size);
      }
    },
    [propOnPageSizeChange, tablePrefsKey, persistPageSize]
  );

  // ============================================================================
  // QUICK FILTER STATE
  // ============================================================================

  const [quickFilterState, setQuickFilterState] = useState<QuickFilterState>({});

  return {
    // Sorting
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    sortColumns,
    setSortColumns,

    // Selection
    selectedItems,
    setSelectedItems,
    showFloatingBulkActions,
    setShowFloatingBulkActions,
    showCalendarModal,
    setShowCalendarModal,

    // Columns
    columnWidths,
    setColumnWidths,
    columnOrder,
    setColumnOrder,
    savedView,
    setSavedView,
    isResizing,
    setIsResizing,
    draggedColumn,
    setDraggedColumn,

    // Inline editing
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    savingCell,
    setSavingCell,

    // Pagination
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,

    // Quick filters
    quickFilterState,
    setQuickFilterState,
  };
}
